import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  KeyRound, ShieldCheck, Monitor, Smartphone, Loader2, Check,
  LogOut, Eye, EyeOff, Laptop, AlertTriangle, Trash2,
} from "lucide-react";
import client, { clearSession } from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";

/** Turn a raw user-agent into a friendly device + browser label. */
function describeDevice(ua) {
  const s = (ua || "").toLowerCase();
  if (!s) return { label: "Unknown device", mobile: false };
  let os = "";
  if (s.includes("iphone")) os = "iPhone";
  else if (s.includes("ipad")) os = "iPad";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("mac os") || s.includes("macintosh")) os = "Mac";
  else if (s.includes("windows")) os = "Windows";
  else if (s.includes("linux")) os = "Linux";
  let browser = "";
  if (s.includes("edg/")) browser = "Edge";
  else if (s.includes("chrome/") && !s.includes("edg/")) browser = "Chrome";
  else if (s.includes("firefox/")) browser = "Firefox";
  else if (s.includes("safari/") && !s.includes("chrome/")) browser = "Safari";
  else if (s.includes("curl")) browser = "API client";
  const mobile = s.includes("iphone") || s.includes("android") || s.includes("ipad");
  const parts = [os, browser].filter(Boolean);
  return { label: parts.length ? parts.join(" · ") : "Web browser", mobile };
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString();
}

function PasswordField({ label, value, onChange, testId, show, onToggle, placeholder }) {
  return (
    <div>
      <label className="k-label mb-1 block">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          className="k-input pr-10"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={testId.includes("current") ? "current-password" : "new-password"}
          data-testid={testId}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 k-btn k-btn-ghost !p-1.5 !px-1.5"
          aria-label={show ? "hide password" : "show password"}
          tabIndex={-1}
          data-testid={`${testId}-toggle`}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </div>
  );
}

/**
 * Security. Change your password and see every device signed in to your
 * account. Changing the password ends every other session. You can also
 * sign out everywhere else in one move, behind a typed confirmation.
 */
export default function SecurityScreen() {
  const nav = useNavigate();

  // Change password
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [pwOk, setPwOk] = useState("");

  // Sessions
  const [sessions, setSessions] = useState(null);
  const [signOutAllOpen, setSignOutAllOpen] = useState(false);
  const [revokingSid, setRevokingSid] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/sessions");
      setSessions(data.sessions || []);
    } catch (_e) {
      setSessions([]);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const newValid = next.length >= 12;
  const matchValid = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && newValid && matchValid && !pwBusy;

  const changePassword = async () => {
    setPwErr(""); setPwOk("");
    if (!newValid) { setPwErr("Your new password needs at least 12 characters."); return; }
    if (!matchValid) { setPwErr("The two new passwords do not match."); return; }
    setPwBusy(true);
    try {
      const { data } = await client.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      const ended = data.other_sessions_ended || 0;
      setPwOk(ended > 0
        ? `Password updated. Signed out ${ended} other device${ended > 1 ? "s" : ""}.`
        : "Password updated.");
      setCurrent(""); setNext(""); setConfirm("");
      loadSessions();
    } catch (e) {
      setPwErr(e?.response?.data?.detail || "That did not go through. Check your current password and try again.");
    } finally {
      setPwBusy(false);
    }
  };

  const revokeOne = async (sid) => {
    setRevokingSid(sid);
    try {
      await client.post("/auth/sessions/revoke", { sid });
      await loadSessions();
    } catch (_e) { /* silent */ } finally {
      setRevokingSid(null);
    }
  };

  const signOutEverywhere = async () => {
    await client.post("/auth/sessions/revoke-all", { keep_current: true });
    setSignOutAllOpen(false);
    await loadSessions();
    setPwOk("Signed out of every other device.");
  };

  const otherCount = (sessions || []).filter((s) => !s.is_current).length;

  const deleteAccount = async () => {
    await client.delete("/account");
    clearSession();
    try { localStorage.removeItem("klareet.report"); } catch (_e) { /* ignore */ }
    setDeleteOpen(false);
    nav("/", { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <div className="k-label mb-2">account</div>
      <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="security-title">
        Security.
      </h1>
      <p className="k-hero-sub mb-6">Your password and the devices signed in to your account.</p>

      {/* Change password */}
      <div className="k-card mb-6" data-testid="change-password-card">
        <div className="k-label mb-3 flex items-center gap-2"><KeyRound size={14} /> change password</div>
        <div className="space-y-3">
          <PasswordField
            label="Current password" value={current} onChange={setCurrent}
            testId="password-current" show={showCur} onToggle={() => setShowCur((v) => !v)}
            placeholder="your current password"
          />
          <PasswordField
            label="New password" value={next} onChange={setNext}
            testId="password-new" show={showNew} onToggle={() => setShowNew((v) => !v)}
            placeholder="at least 12 characters"
          />
          <div>
            <PasswordField
              label="Confirm new password" value={confirm} onChange={setConfirm}
              testId="password-confirm" show={showNew} onToggle={() => setShowNew((v) => !v)}
              placeholder="type it again"
            />
            {next.length > 0 && (
              <div className="mt-1.5 flex items-center gap-3 text-xs">
                <span style={{ color: newValid ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-muted))" }} data-testid="password-len-hint">
                  {newValid ? <Check size={12} className="inline" /> : null} 12+ characters
                </span>
                {confirm.length > 0 && (
                  <span style={{ color: matchValid ? "hsl(var(--klareet-teal))" : "hsl(var(--destructive))" }} data-testid="password-match-hint">
                    {matchValid ? <><Check size={12} className="inline" /> passwords match</> : "passwords do not match"}
                  </span>
                )}
              </div>
            )}
          </div>
          {pwErr && <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="password-error">{pwErr}</div>}
          {pwOk && <div className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-teal))" }} data-testid="password-success"><Check size={14} /> {pwOk}</div>}
          <div className="pt-1">
            <button onClick={changePassword} disabled={!canSubmit} className="k-btn k-btn-primary" data-testid="change-password-submit">
              {pwBusy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={14} />}
              {pwBusy ? "Updating" : "Update password"}
            </button>
            <p className="k-quiet !text-xs mt-2">Changing your password signs out every other device automatically.</p>
          </div>
        </div>
      </div>

      {/* Active sessions */}
      <div className="k-card" data-testid="sessions-card">
        <div className="flex items-center justify-between mb-3">
          <div className="k-label !mb-0 flex items-center gap-2"><ShieldCheck size={14} /> active sessions</div>
          {otherCount > 0 && (
            <button
              onClick={() => setSignOutAllOpen(true)}
              className="k-btn k-btn-ghost !py-1.5 !px-2.5 !text-xs"
              style={{ color: "hsl(var(--destructive))" }}
              data-testid="sign-out-everywhere-btn"
            >
              <LogOut size={13} /> Sign out everywhere else
            </button>
          )}
        </div>

        {sessions === null ? (
          <div className="py-8 text-center"><Loader2 size={20} className="animate-spin mx-auto" style={{ color: "hsl(var(--klareet-muted))" }} /></div>
        ) : sessions.length === 0 ? (
          <div className="k-quiet text-sm py-2" data-testid="sessions-empty">No active sessions to show.</div>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map((s) => {
              const d = describeDevice(s.user_agent);
              const DeviceIcon = d.mobile ? Smartphone : (d.label.includes("Mac") || d.label.includes("Windows") ? Laptop : Monitor);
              return (
                <div
                  key={s.sid}
                  className="flex items-center gap-3 rounded-xl border p-3"
                  style={{ borderColor: "hsl(var(--klareet-line-soft))", background: s.is_current ? "hsl(var(--klareet-teal) / 0.06)" : "hsl(var(--klareet-canvas-2))" }}
                  data-testid={`session-row${s.is_current ? "-current" : ""}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: "hsl(var(--klareet-ink))", color: "white" }}
                  >
                    <DeviceIcon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>{d.label}</span>
                      {s.is_current && (
                        <span className="k-chip !text-[10px]" style={{ background: "hsl(var(--klareet-teal) / 0.15)", color: "hsl(var(--klareet-teal))" }} data-testid="session-current-badge">
                          This device
                        </span>
                      )}
                    </div>
                    <div className="k-quiet !text-xs">
                      Active {timeAgo(s.last_active_at)}
                      {s.started_at ? ` · signed in ${timeAgo(s.started_at)}` : ""}
                    </div>
                  </div>
                  {!s.is_current && (
                    <button
                      onClick={() => revokeOne(s.sid)}
                      disabled={revokingSid === s.sid}
                      className="k-btn k-btn-ghost !py-1.5 !px-2.5 !text-xs shrink-0"
                      data-testid="session-revoke-btn"
                    >
                      {revokingSid === s.sid ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
                      Sign out
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={signOutAllOpen}
        onCancel={() => setSignOutAllOpen(false)}
        onConfirm={signOutEverywhere}
        title="Sign out everywhere else?"
        description="This signs out every other device on your account. This device stays signed in."
        consequences={[
          "Every other browser and phone is signed out right away",
          "Anyone using a session you did not start loses access",
          "You can sign back in on those devices any time",
        ]}
        confirmWord="SIGN OUT"
        confirmLabel="Sign out others"
        tone="warning"
        testId="signout-all-confirm"
      />

      {/* Danger zone */}
      <div
        className="k-card mt-6"
        style={{ borderColor: "hsl(var(--destructive) / 0.4)" }}
        data-testid="danger-zone-card"
      >
        <div className="k-label mb-1 flex items-center gap-2" style={{ color: "hsl(var(--destructive))" }}>
          <AlertTriangle size={14} /> danger zone
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <div className="text-sm font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>Delete your account</div>
            <p className="k-quiet !text-xs mt-0.5" style={{ maxWidth: "34rem" }}>
              Permanently removes your account and every piece of your data: your reads, answers, goals, journal, resume, and photo. This cannot be undone.
            </p>
          </div>
          <button
            onClick={() => setDeleteOpen(true)}
            className="k-btn shrink-0"
            style={{ background: "hsl(var(--destructive))", color: "white" }}
            data-testid="delete-account-btn"
          >
            <Trash2 size={14} /> Delete account
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={deleteAccount}
        title="Delete your account?"
        description="This permanently deletes your account and all of your data. There is no way to get it back."
        consequences={[
          "Your reads, answers, goals, journal, resume, and photo are erased",
          "You are signed out of every device",
          "This is final: it cannot be undone",
        ]}
        confirmWord="DELETE"
        confirmLabel="Delete my account"
        tone="danger"
        testId="delete-account-confirm"
      />
    </div>
  );
}
